import React from 'react';
import DemoStage from '../DemoStage.jsx';

/**
 * The Step-2 severity cascade: picking a different severity code repopulates
 * the incident-category list and moves the reference-rail highlight onto the
 * matching code card — the coupling NewCaseWizard.jsx and TreatmentGuide.jsx
 * drive from config. Card styling borrows the real rail's active treatment
 * (brand border + weak fill) so the demo can't drift from the actual look.
 * Category names are generic placeholders — the real lists are config-driven
 * per treatment and code.
 */

const CATEGORIES = {
  'Code 1': ['Erythema', 'Mild rash', 'Dry skin', 'Other'],
  'Code 3': ['Blistering', 'Burn', 'Grazing', 'Other'],
};

const STEPS = [
  {
    caption: 'With Code 1 selected, the incident-category list shows Code 1 conditions.',
    scene: 'code1', ms: 2600,
    cursor: 'sev',
  },
  {
    caption: 'The reaction is more serious than first thought — change the code.',
    scene: 'code1', ms: 1800,
    cursor: 'sev', click: true,
  },
  {
    caption: 'Code 3 selected — the category list repopulates to match the new code.',
    scene: 'code3', ms: 2600,
    cursor: 'cats',
  },
  {
    caption: 'The reference rail highlights the Code 3 card so you can compare against its example photo.',
    scene: 'code3', ms: 3200,
    cursor: 'guide-3',
  },
];

function GuideCard({ n, code, active, target }) {
  return (
    <div data-kb-target={target} style={{
      border: '1px solid',
      borderColor: active ? 'var(--brand)' : 'var(--border)',
      borderRadius: 8, overflow: 'hidden',
      background: active ? 'var(--brand-weak)' : 'var(--surface)',
      boxShadow: active ? '0 0 0 3px rgba(99,102,241,.10)' : 'none',
      opacity: active ? 1 : 0.72,
      transition: 'border-color 200ms var(--ease), background 200ms var(--ease), opacity 200ms var(--ease), box-shadow 200ms var(--ease)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px' }}>
        <span style={{
          display: 'grid', placeItems: 'center', minWidth: 18, height: 18,
          borderRadius: 5, fontSize: 10, fontWeight: 700,
          background: active ? 'var(--brand)' : 'var(--border)',
          color: active ? '#fff' : 'var(--text-2)',
          transition: 'background 200ms var(--ease)',
        }}>{n}</span>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)' }}>{code}</span>
      </div>
      {/* example-photo placeholder */}
      <div style={{ height: 22, background: 'var(--surface-3)', borderTop: '1px solid var(--border)' }} />
    </div>
  );
}

export default function SeverityCascadeDemo() {
  return (
    <DemoStage steps={STEPS} label="Severity code drives the categories and the reference rail" height={240}>
      {(step) => {
        const scene = step?.scene ?? 'code1';
        const code = scene === 'code3' ? 'Code 3' : 'Code 1';
        const cats = CATEGORIES[code];
        return (
          <div className="kb-mock" style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            {/* Form slice — severity select + dependent category list */}
            <div style={{ flex: '1.5 1 0', minWidth: 0 }}>
              <div style={{ marginBottom: 10 }}>
                <div className="kb-mock-label">Severity code</div>
                <div className="kb-mock-input" data-kb-target="sev" style={{ justifyContent: 'space-between', fontWeight: 600 }}>
                  <span key={code} className="kb-pop">{code}</span>
                  <span style={{ color: 'var(--text-3)', fontSize: 10 }}>▾</span>
                </div>
              </div>
              <div>
                <div className="kb-mock-label">Incident category</div>
                <div data-kb-target="cats" style={{
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 8, overflow: 'hidden',
                }}>
                  <div key={code} className="kb-pop">
                    {cats.map((c, i) => (
                      <div key={c} style={{
                        padding: '6px 10px', fontSize: 12.5, color: 'var(--text-2)',
                        borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                      }}>{c}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Mini reference rail — highlight follows the selected code */}
            <div style={{ flex: '1 1 0', minWidth: 0 }}>
              <div className="kb-mock-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>LHR AE codes</span>
                <span style={{ fontWeight: 400 }}>Reference</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <GuideCard n={1} code="Code 1" active={code === 'Code 1'} />
                <GuideCard n={2} code="Code 2" active={false} />
                <GuideCard n={3} code="Code 3" active={code === 'Code 3'} target="guide-3" />
              </div>
            </div>
          </div>
        );
      }}
    </DemoStage>
  );
}
